import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GifLoader } from '../website/GifLoader';

interface ContentGenerationLoaderProps {
  isVisible: boolean;
  hasImage: boolean;
}

export function ContentGenerationLoader({ isVisible, hasImage }: ContentGenerationLoaderProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<string>('');
  const [stepIndex, setStepIndex] = useState(0);

  const baseGenerationSteps = [
    { message: t('contentCreation.generation.steps.analyzing', "Analyzing your brand voice and style..."), duration: 1500 },
    { message: t('contentCreation.generation.steps.creating', "Creating engaging content tailored to your audience..."), duration: 1500 },
    { message: t('contentCreation.generation.steps.optimizing', "Optimizing for your selected platforms..."), duration: 1500 },
    { message: t('contentCreation.generation.steps.hashtags', "Adding hashtags and keywords for visibility..."), duration: 1500 },
    { message: t('contentCreation.generation.steps.polishing', "Polishing and finalizing your content..."), duration: 1500 },
  ];
  
  const imageGenerationSteps = [
    { message: t('contentCreation.generation.steps.generatingImage', "Generating creative imagery for your content..."), duration: 1800 },
    { message: t('contentCreation.generation.steps.designingVisuals', "Designing visuals that match your brand identity..."), duration: 1800 },
  ];
  
  // Combine base steps with image steps if hasImage is true
  const generationSteps = hasImage 
    ? [...baseGenerationSteps.slice(0, 2), ...imageGenerationSteps, ...baseGenerationSteps.slice(2)]
    : baseGenerationSteps;

  useEffect(() => {
    if (!isVisible) {
      setStepIndex(0);
      setCurrentStep('');
      return;
    }

    // Reset to first step when becoming visible
    setStepIndex(0);
    setCurrentStep(generationSteps[0].message);

    // Set up the step transition
    const intervalId = setInterval(() => {
      setStepIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        
        // If we've gone through all steps, cycle back to the first step
        if (nextIndex >= generationSteps.length) {
          // Update the current step message to the first one again
          setCurrentStep(generationSteps[0].message);
          return 0;
        }
        
        // Update the current step message
        setCurrentStep(generationSteps[nextIndex].message);
        return nextIndex;
      });
    }, 1800); // Change step every 1.8 seconds

    return () => clearInterval(intervalId);
  }, [isVisible, hasImage, generationSteps]);

  if (!isVisible) return null;

  return (
    <GifLoader
      title={t('contentCreation.generation.title', "Creating Your AI Content")}
      currentStep={currentStep}
    />
  );
} 